<template>
  <section
    ref="sectionEl"
    id="websites"
    class="relative py-28 bg-gray-50 dark:bg-dark overflow-hidden transition-colors duration-300"
    :class="{ 'is-visible': isVisible }"
  >
    <!-- Ambient glow -->
    <div class="absolute top-0 right-0 w-1/2 h-full pointer-events-none">
      <div class="absolute top-1/4 right-0 w-96 h-96 rounded-full bg-blue-500/[0.05] blur-3xl"></div>
    </div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

        <!-- Left: Text content -->
        <div class="anim-el d1">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6">
            {{ t('websiteSolution.specialOffer') }}
          </p>

          <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-5 transition-colors duration-300">
            {{ t('solutions.websites.title') }}<br />
            <span class="gradient-text">che convertono davvero.</span>
          </h2>

          <p class="text-lg text-gray-500 dark:text-gray-400 leading-relaxed mb-10 max-w-lg transition-colors duration-300">
            {{ t('solutions.websites.description') }}
          </p>

          <div class="space-y-4 mb-10">
            <div v-for="feature in keyFeatures" :key="feature.title" class="flex items-start gap-4">
              <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center mt-0.5 transition-colors duration-300">
                <component :is="feature.icon" class="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white mb-0.5 transition-colors duration-300">{{ feature.title }}</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ feature.description }}</p>
              </div>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row items-start gap-3">
            <router-link
              to="/contact"
              class="inline-flex items-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[15px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
            >
              {{ t('websiteSolution.cta.primary') }}
              <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </router-link>
            <router-link
              to="/preventivo-sito-web"
              class="inline-flex items-center h-12 px-7 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium text-[15px] rounded-xl border border-gray-200 dark:border-white/[0.08] hover:border-gray-400 dark:hover:border-white/20 transition-all"
            >
              {{ t('websiteSolution.cta.quote') }}
            </router-link>
          </div>
        </div>

        <!-- Right: Animated browser mockup + floating badges -->
        <div class="relative hidden lg:block anim-el d2">
          <!-- Background glow -->
          <div class="absolute -inset-20 pointer-events-none">
            <div class="w-64 h-64 rounded-full bg-blue-500/[0.06] blur-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
          </div>

          <!-- Browser mockup -->
          <div class="rounded-xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-xl bg-white dark:bg-dark transition-colors duration-300">
            <!-- Chrome bar -->
            <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
              <div class="flex gap-1.5">
                <div class="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/80"></div>
                <div class="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
              </div>
              <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-8 flex items-center px-3">
                <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                <span class="text-[11px] text-gray-400 dark:text-gray-500">tuodominio.com</span>
              </div>
            </div>

            <!-- Website wireframe -->
            <div class="p-5 space-y-4">
              <!-- Nav -->
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-bold text-gray-900/60 dark:text-white/40 tracking-wide">TuoBrand</span>
                <div class="flex gap-4 items-center">
                  <span class="text-[9px] text-gray-400 dark:text-gray-500">Servizi</span>
                  <span class="text-[9px] text-gray-400 dark:text-gray-500">Portfolio</span>
                  <div class="h-6 w-16 rounded bg-blue-500/80 flex items-center justify-center">
                    <span class="text-[8px] text-white font-bold">Contatti</span>
                  </div>
                </div>
              </div>

              <!-- Hero area -->
              <div class="pt-4 pb-2">
                <p class="text-sm font-bold text-gray-900/70 dark:text-white/60 mb-1">Il tuo business merita</p>
                <p class="text-sm font-bold text-gray-900/70 dark:text-white/60 mb-3">un sito <span class="text-blue-500">che converte.</span></p>
                <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-1">Design moderno, SEO ottimizzato e velocità fulminea.</p>
                <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-5">Trasforma i visitatori in clienti oggi stesso.</p>
                <div class="flex gap-3">
                  <div class="h-8 w-28 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center">
                    <span class="text-[8px] font-bold text-white dark:text-gray-900">Inizia Ora</span>
                  </div>
                  <div class="h-8 w-24 rounded-lg border border-gray-200 dark:border-white/[0.1] flex items-center justify-center">
                    <span class="text-[8px] font-bold text-gray-500 dark:text-gray-400">Portfolio</span>
                  </div>
                </div>
              </div>

              <!-- Feature cards -->
              <div class="grid grid-cols-3 gap-3">
                <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                  <div class="w-6 h-6 rounded bg-blue-500/10 flex items-center justify-center mb-2">
                    <Zap class="w-3 h-3 text-blue-500" />
                  </div>
                  <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">Velocità</p>
                  <p class="text-[7px] text-gray-400 dark:text-gray-500">Caricamento &lt;1s</p>
                </div>
                <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                  <div class="w-6 h-6 rounded bg-green-500/10 flex items-center justify-center mb-2">
                    <Search class="w-3 h-3 text-green-500" />
                  </div>
                  <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">SEO</p>
                  <p class="text-[7px] text-gray-400 dark:text-gray-500">Prima pagina Google</p>
                </div>
                <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                  <div class="w-6 h-6 rounded bg-purple-500/10 flex items-center justify-center mb-2">
                    <TrendingUp class="w-3 h-3 text-purple-500" />
                  </div>
                  <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">Conversioni</p>
                  <p class="text-[7px] text-gray-400 dark:text-gray-500">+300% lead</p>
                </div>
              </div>

              <!-- Performance score bar -->
              <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-[9px] text-gray-500 dark:text-gray-400 font-medium">Performance score</span>
                  <span class="text-[9px] font-bold text-green-500">98 / 100</span>
                </div>
                <div class="h-1.5 rounded-full bg-gray-200 dark:bg-white/[0.08] overflow-hidden">
                  <div class="h-full rounded-full bg-gradient-to-r from-green-400 to-green-500 score-bar"></div>
                </div>
                <div class="flex justify-between mt-1.5">
                  <span class="text-[7px] text-gray-400">Performance</span>
                  <span class="text-[7px] text-gray-400">Accessibilità</span>
                  <span class="text-[7px] text-gray-400">SEO</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Floating metric badges -->
          <div class="absolute -top-4 -right-4 badge-float fb1">
            <div class="rounded-xl bg-white dark:bg-dark border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center">
                <Zap class="w-3.5 h-3.5 text-green-500" />
              </div>
              <div>
                <div class="text-[10px] text-gray-400 dark:text-gray-500">PageSpeed</div>
                <div class="text-xs font-bold text-gray-900 dark:text-white">98 / 100</div>
              </div>
            </div>
          </div>

          <div class="absolute -bottom-4 -left-4 badge-float fb2">
            <div class="rounded-xl bg-white dark:bg-dark border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Search class="w-3.5 h-3.5 text-blue-500" />
              </div>
              <div>
                <div class="text-[10px] text-gray-400 dark:text-gray-500">SEO Score</div>
                <div class="text-xs font-bold text-gray-900 dark:text-white">A+</div>
              </div>
            </div>
          </div>

          <div class="absolute top-1/2 -right-6 -translate-y-1/2 badge-float fb3">
            <div class="rounded-xl bg-white dark:bg-dark border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-purple-500/10 flex items-center justify-center">
                <TrendingUp class="w-3.5 h-3.5 text-purple-500" />
              </div>
              <div>
                <div class="text-[10px] text-gray-400 dark:text-gray-500">Conversioni</div>
                <div class="text-xs font-bold text-gray-900 dark:text-white">+300%</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Smartphone, Search, Zap, Bot, ArrowRight, Lock, TrendingUp } from 'lucide-vue-next'

const { t } = useI18n()

const sectionEl = ref(null)
const isVisible = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const keyFeatures = computed(() => [
  { icon: Smartphone, title: t('solutions.cards.websites.responsive.title'), description: t('solutions.cards.websites.responsive.description') },
  { icon: Search, title: t('solutions.cards.websites.seo.title'), description: t('solutions.cards.websites.seo.description') },
  { icon: Zap, title: t('solutions.cards.websites.performance.title'), description: t('solutions.cards.websites.performance.description') },
  { icon: Bot, title: t('websiteSolution.freeChatbot.title'), description: t('websiteSolution.freeChatbot.description') }
])
</script>

<style scoped>
.gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.18s; }

/* Animated performance score bar */
.score-bar {
  width: 0;
  transition: width 1.4s cubic-bezier(.22,1,.36,1) 0.6s;
}
.is-visible .score-bar { width: 98%; }

/* Floating badges animation */
.badge-float {
  animation: floatBadge 3.5s ease-in-out infinite;
}
.fb1 { animation-delay: 0s; }
.fb2 { animation-delay: 1.2s; }
.fb3 { animation-delay: 2.1s; }

@keyframes floatBadge {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}

/* fb3 also needs the -translate-y-1/2 base */
.fb3 {
  animation-name: floatBadgeMid;
}
@keyframes floatBadgeMid {
  0%, 100% { transform: translateY(-50%); }
  50% { transform: translateY(calc(-50% - 7px)); }
}
</style>
